import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../services/authContext';
import { adminUserService } from '../services/adminUserService';
import './AdminUsersPage.css';

const AdminUsersPage = () => {
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(10);
  
  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  
  // Modal
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [modalAction, setModalAction] = useState(''); // 'edit', 'delete', 'status', 'role', 'create', 'reset-password'

  // Form
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phoneNumber: '',
    address: '',
    role: 'user',
    status: 'active',
    newPassword: ''
  });

  useEffect(() => {
    loadUsers();
  }, [currentPage, limit, search, statusFilter, roleFilter]);

  const loadUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminUserService.getAllUsers(
        currentPage,
        limit,
        search,
        statusFilter,
        roleFilter
      );
      setUsers(data.users);
      setTotalPages(data.pagination.pages);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filterType, value) => {
    setCurrentPage(1);
    if (filterType === 'status') {
      setStatusFilter(value);
    } else if (filterType === 'role') {
      setRoleFilter(value);
    }
  };

  const openModal = (userObj, action) => {
    setSelectedUser(userObj);
    setModalAction(action);
    if (action === 'edit') {
      setFormData({
        name: userObj.name,
        email: userObj.email,
        password: '',
        phoneNumber: userObj.phoneNumber || '',
        address: userObj.address || '',
        role: userObj.role,
        status: userObj.status
      });
    } else if (action === 'create') {
      setFormData({
        name: '',
        email: '',
        password: '',
        phoneNumber: '',
        address: '',
        role: 'user',
        status: 'active',
        newPassword: ''
      });
    } else if (action === 'reset-password') {
      setFormData({
        ...formData,
        newPassword: ''
      });
    }
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedUser(null);
    setModalAction('');
    setFormData({
      name: '',
      email: '',
      password: '',
      phoneNumber: '',
      address: '',
      role: 'user',
      status: 'active',
      newPassword: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleModalSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      let result;
      switch (modalAction) {
        case 'create':
          result = await adminUserService.createUser(formData);
          setSuccess(result.message);
          break;
        case 'edit':
          result = await adminUserService.updateUser(selectedUser._id, formData);
          setSuccess(result.message);
          break;
        case 'delete':
          result = await adminUserService.deleteUser(selectedUser._id);
          setSuccess(result.message);
          break;
        case 'status':
          result = await adminUserService.changeUserStatus(selectedUser._id, formData.status);
          setSuccess(result.message);
          break;
        case 'make-admin':
          result = await adminUserService.makeUserAdmin(selectedUser._id);
          setSuccess(result.message);
          break;
        case 'remove-admin':
          result = await adminUserService.removeAdminPrivileges(selectedUser._id);
          setSuccess(result.message);
          break;
        case 'reset-password':
          result = await adminUserService.resetUserPassword(selectedUser._id, formData.newPassword);
          setSuccess(result.message);
          break;
        default:
          break;
      }
      closeModal();
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${modalAction} user`);
    }
  };

  const getRoleBadge = (role) => {
    return (
      <span className={`badge badge-role badge-${role}`}>
        {role.toUpperCase()}
      </span>
    );
  };

  const getStatusBadge = (status) => {
    return (
      <span className={`badge badge-status badge-${status}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="page-container">
      <div className="admin-users-container">
        <div className="page-header-section">
          <h2>Kelola Pengguna</h2>
          <button 
            className="btn btn-primary"
            onClick={() => openModal(null, 'create')}
          >
            + Buat Pengguna
          </button>
        </div>
        
        {error && <div className="alert alert-error">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        {/* Filters */}
        <div className="filters-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Cari berdasarkan nama atau email..."
              value={search}
              onChange={handleSearch}
              className="search-input"
            />
          </div>

          <div className="filter-controls">
            <select
              value={statusFilter}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="filter-select"
            >
              <option value="">Semua Status</option>
              <option value="active">Aktif</option>
              <option value="inactive">Tidak Aktif</option>
            </select>

            <select
              value={roleFilter}
              onChange={(e) => handleFilterChange('role', e.target.value)}
              className="filter-select"
            >
              <option value="">Semua Peran</option>
              <option value="admin">Admin</option>
              <option value="user">Pengguna</option>
            </select>

            <select
              value={limit}
              onChange={(e) => {
                setLimit(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              className="filter-select"
            >
              <option value="10">10 per halaman</option>
              <option value="25">25 per halaman</option>
              <option value="50">50 per halaman</option>
              <option value="100">100 per halaman</option>
            </select>
          </div>
        </div>

        {/* Users Table */}
        {loading ? (
          <div className="loading">Memuat pengguna...</div>
        ) : users.length === 0 ? (
          <div className="no-users">Pengguna tidak ditemukan</div>
        ) : (
          <>
            <div className="users-table-wrapper">
              <table className="users-table">
                <thead>
                  <tr>
                    <th>Nama</th>
                    <th>Email</th>
                    <th>Telepon</th>
                    <th>Peran</th>
                    <th>Status</th>
                    <th>Bergabung</th>
                    <th>Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((userItem) => (
                    <tr key={userItem._id} className={`user-row ${userItem.status}`}>
                      <td className="user-name">{userItem.name}</td>
                      <td className="user-email">{userItem.email}</td>
                      <td className="user-phone">{userItem.phoneNumber || '-'}</td>
                      <td className="user-role">{getRoleBadge(userItem.role)}</td>
                      <td className="user-status">{getStatusBadge(userItem.status)}</td>
                      <td className="user-date">
                        {new Date(userItem.createdAt).toLocaleDateString()}
                      </td>
                      <td className="user-actions">
                        {user._id !== userItem._id && (
                          <>
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => openModal(userItem, 'edit')}
                              title="Ubah pengguna"
                            >
                              Ubah
                            </button>

                            {userItem.status === 'active' ? (
                              <button
                                className="btn btn-sm btn-warning"
                                onClick={() => {
                                  setSelectedUser(userItem);
                                  setFormData({ ...formData, status: 'inactive' });
                                  setModalAction('status');
                                  setShowModal(true);
                                }}
                                title="Nonaktifkan pengguna"
                              >
                                Nonaktifkan
                              </button>
                            ) : (
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => {
                                  setSelectedUser(userItem);
                                  setFormData({ ...formData, status: 'active' });
                                  setModalAction('status');
                                  setShowModal(true);
                                }}
                                title="Aktifkan pengguna"
                              >
                                Aktifkan
                              </button>
                            )}

                            {userItem.role === 'user' ? (
                              <button
                                className="btn btn-sm btn-info"
                                onClick={() => {
                                  setSelectedUser(userItem);
                                  setModalAction('make-admin');
                                  setShowModal(true);
                                }}
                                title="Jadikan admin"
                              >
                                Jadikan Admin
                              </button>
                            ) : (
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => {
                                  setSelectedUser(userItem);
                                  setModalAction('remove-admin');
                                  setShowModal(true);
                                }}
                                title="Hapus hak admin"
                              >
                                Hapus Admin
                              </button>
                            )}

                            <button
                              className="btn btn-sm btn-warning"
                              onClick={() => openModal(userItem, 'reset-password')}
                              title="Reset kata sandi"
                            >
                              Reset Password
                            </button>

                            <button
                              className="btn btn-sm btn-danger"
                              onClick={() => openModal(userItem, 'delete')}
                              title="Hapus pengguna"
                            >
                              Hapus
                            </button>
                          </>
                        )}
                        {user._id === userItem._id && (
                          <span className="badge badge-info">Anda</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination">
              <button
                className="btn btn-secondary"
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
              >
                Sebelumnya
              </button>

              <span className="page-info">
                Halaman {currentPage} dari {totalPages}
              </span>

              <button
                className="btn btn-secondary"
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
              >
                Berikutnya
              </button>
            </div>
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            {modalAction === 'delete' ? (
              <>
                <h3>Hapus Pengguna</h3>
                <p>Apakah Anda yakin ingin menghapus <strong>{selectedUser?.name}</strong>?</p>
                <p className="text-danger">Tindakan ini tidak dapat dibatalkan.</p>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={closeModal}>
                    Batal
                  </button>
                  <button className="btn btn-danger" onClick={handleModalSubmit}>
                    Hapus Pengguna
                  </button>
                </div>
              </>
            ) : modalAction === 'status' ? (
              <>
                <h3>Ubah Status Pengguna</h3>
                <p>Ubah status <strong>{selectedUser?.name}</strong> menjadi <strong>{formData.status === 'active' ? 'Aktif' : 'Tidak Aktif'}</strong>?</p>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={closeModal}>
                    Batal
                  </button>
                  <button className="btn btn-primary" onClick={handleModalSubmit}>
                    Ubah Status
                  </button>
                </div>
              </>
            ) : modalAction === 'make-admin' ? (
              <>
                <h3>Jadikan Admin</h3>
                <p>Promosikan <strong>{selectedUser?.name}</strong> menjadi admin?</p>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={closeModal}>
                    Batal
                  </button>
                  <button className="btn btn-primary" onClick={handleModalSubmit}>
                    Jadikan Admin
                  </button>
                </div>
              </>
            ) : modalAction === 'remove-admin' ? (
              <>
                <h3>Hapus Hak Admin</h3>
                <p>Hapus hak admin dari <strong>{selectedUser?.name}</strong>?</p>
                <div className="modal-actions">
                  <button className="btn btn-secondary" onClick={closeModal}>
                    Batal
                  </button>
                  <button className="btn btn-primary" onClick={handleModalSubmit}>
                    Hapus Admin
                  </button>
                </div>
              </>
            ) : modalAction === 'reset-password' ? (
              <>
                <h3>Reset Kata Sandi</h3>
                <p>Reset kata sandi untuk <strong>{selectedUser?.name}</strong></p>
                <form onSubmit={handleModalSubmit} className="modal-form">
                  <div className="form-group">
                    <label>Kata Sandi Baru</label>
                    <input
                      type="password"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Minimal 6 karakter"
                      required
                      minLength="6"
                    />
                  </div>
                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closeModal}
                    >
                      Batal
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Reset Password
                    </button>
                  </div>
                </form>
              </>
            ) : modalAction === 'create' ? (
              <>
                <h3>Buat Pengguna</h3>
                <form onSubmit={handleModalSubmit} className="modal-form">
                  <div className="form-group">
                    <label>Nama</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Kata Sandi</label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Nomor Telepon</label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>Alamat</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="form-input"
                      rows="3"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Peran</label>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        className="form-input"
                      >
                        <option value="user">Pengguna</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Status</label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="form-input"
                      >
                        <option value="active">Aktif</option>
                        <option value="inactive">Tidak Aktif</option>
                      </select>
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closeModal}
                    >
                      Batal
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Buat Pengguna
                    </button>
                  </div>
                </form>
              </>
            ) : (
              <>
                <h3>Ubah Pengguna</h3>
                <form onSubmit={handleModalSubmit} className="modal-form">
                  <div className="form-group">
                    <label>Nama</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Nomor Telepon</label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>Alamat</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="form-input"
                      rows="3"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Peran</label>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        className="form-input"
                        disabled={selectedUser?._id === user._id}
                      >
                        <option value="user">Pengguna</option>
                        <option value="admin">Admin</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Status</label>
                      <select
                        name="status"
                        value={formData.status}
                        onChange={handleInputChange}
                        className="form-input"
                      >
                        <option value="active">Aktif</option>
                        <option value="inactive">Tidak Aktif</option>
                      </select>
                    </div>
                  </div>

                  <div className="modal-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={closeModal}
                    >
                      Batal
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Perbarui Pengguna
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminUsersPage;
